import React from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Circle, Check, FileText } from 'lucide-react'

interface TimelineMessage {
  id: string
  body: string
  messageType: string
  createdAt: string
  metadata?: Record<string, unknown>
  milestone?: {
    id: string
    name: string
    slug?: string | null
  } | null
  author?: {
    id: string
    name: string
    avatar: string | null
  } | null
}

interface TimelineCardProps {
  timeline: TimelineMessage[]
  onViewSubmission: (submissionId: string) => void
}

const formatDate = (dateString: string) => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(dateString))
}

const normalizeTimelineBody = (body: string) => {
  return body
    .replace(/^Submitted\s+submit\s+/i, 'Submitted ')
    .replace(/^Resubmitted\s+submit\s+/i, 'Resubmitted ')
}

const getSubmissionIdFromMetadata = (metadata: unknown): string | null => {
  if (!metadata) return null

  let parsed = metadata
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed)
    } catch {
      return null
    }
  }

  if (!parsed || typeof parsed !== 'object') return null

  const value = (parsed as Record<string, unknown>).submission_id
  if (typeof value === 'number') return String(value)
  if (typeof value === 'string' && value.trim().length > 0) return value
  return null
}

export const TimelineCard: React.FC<TimelineCardProps> = ({ timeline, onViewSubmission }) => {
  if (!timeline || timeline.length === 0) {
    return null
  }

  return (
    <div className="bg-card border border-foreground/10 rounded-2xl p-4 shadow-[0_1px_2px_rgba(30,41,59,0.04)]">
      <p className="text-xs font-semibold tracking-wider text-gray-500 uppercase mb-4">
        Activity Timeline
      </p>
      <div className="space-y-2">
        {timeline.map((message, idx) => {
          const isLast = idx === timeline.length - 1
          return (
            <div key={message.id} className="relative flex gap-2 py-2">
              {/* Timeline line */}
              {!isLast && <div className="absolute left-3 top-8 bottom-0 w-0.5 bg-muted" />}

              {/* Icon */}
              <div className="shrink-0 z-10">
                {message.messageType === 'system' && (
                  <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <Circle className="h-3 w-3 text-blue-600" />
                  </div>
                )}
                {message.messageType === 'status_change' && (
                  <div className="w-6 h-6 rounded-full bg-[#C0FD72]/20 flex items-center justify-center">
                    <Check className="h-3 w-3 text-[#3F7A0A]" />
                  </div>
                )}
                {message.messageType === 'review' && (
                  <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <FileText className="h-3 w-3 text-purple-600" />
                  </div>
                )}
                {message.messageType === 'user' && message.author && (
                  <Avatar className="h-6 w-6 border border-foreground/10">
                    {message.author.avatar && <AvatarImage src={message.author.avatar} />}
                    <AvatarFallback className="text-xs bg-muted text-gray-700">
                      {message.author.name.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <p className="text-sm font-medium text-foreground">
                    {normalizeTimelineBody(message.body)}
                  </p>
                  {message.milestone && (
                    <span className="text-xs text-gray-500">· {message.milestone.name}</span>
                  )}
                  {message.author && (
                    <span className="text-xs text-gray-500">· {message.author.name}</span>
                  )}
                  <span className="text-xs text-gray-500">· {formatDate(message.createdAt)}</span>
                </div>
                {getSubmissionIdFromMetadata(message.metadata) &&
                  message.messageType !== 'review' &&
                  message.milestone?.slug !== 'license_content' && (
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-xs text-[#3F7A0A] hover:text-[#3F7A0A]/80"
                      onClick={() => {
                        const submissionId = getSubmissionIdFromMetadata(message.metadata)
                        if (submissionId) onViewSubmission(submissionId)
                      }}
                    >
                      View submission →
                    </Button>
                  )}

                {message.metadata &&
                  typeof message.metadata === 'object' &&
                  'post_links' in message.metadata &&
                  Array.isArray(message.metadata.post_links) &&
                  (message.metadata.post_links as string[]).map((link, i) => (
                    <Button
                      key={i}
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-xs text-[#3F7A0A] hover:text-[#3F7A0A]/80"
                      asChild
                    >
                      <a href={link} target="_blank" rel="noopener noreferrer">
                        View Live Link
                        {(message.metadata as any).post_links.length > 1 ? ` (${i + 1})` : ''} →
                      </a>
                    </Button>
                  ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
